/**
 * A notice's sheet: something the player must take in, in the toast's anatomy
 * at a dialog's size. A round mark in the notice's tone beside its title — a
 * kicker over the title when it names what the notice is about — what
 * happened under the title in one block of words (a thank-you or what happens
 * next is part of it, never a quieter line of its own beside it), a fact on a
 * well, what is folded under the words, the actions along the foot — a lone
 * one the width of the words, under them — a quiet line at its very foot, and
 * a hairline along the foot while it waits to act by itself. Every line under
 * the title starts where the title does.
 *
 *   danger   coral: something stands in the way — the connection lost, a removal, a failure
 *   accent   someone, or something, of the player's own — the session's end, a moderator asking for them
 *   neutral  the soft ink: it only asks — the full screen a session wants
 *
 * NoticeDialog opens it over the page. Laid on the page it is the page's own
 * content: what stands where something could not be drawn — the whole game on
 * the ground, a section in the main column — the section's name the
 * `kicker`, the message the failure threw folded in `details`, the version
 * line its `foot`. There it is an `alert`, so it is heard as it appears.
 *
 *   <NoticeSheet role="alert" tone="danger" glyph={GLYPH.exclamation}
 *                kicker="Fleet" title="This page ran into a problem" description="…"
 *                details={<Disclosure summary="Details">…</Disclosure>}
 *                actions={<Button tone="primary">Back to the globe</Button>} />
 *
 * `fact` is the hover card's fact row on a well: what it is, and its value at
 * the far end — the time before a removed player can come back. `clock` is
 * the FootClock along its foot — the time before an invitation accepts
 * itself — drawn from where it stands (`elapsedMs`), which its domain keeps;
 * the caller says the same time in words. The sheet's frame does not scroll —
 * its words do, in a box of their own — so the clock stays on the foot.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';

import { FootClock, type FootClockProps } from './foot-clock';
import type { Drawing } from './glyphs';
import { HoverCardFact } from './hover-card';
import { ToneMark } from './tone-mark';
import utilities from '../../styles/utilities.module.css';
import styles from './notice-sheet.module.css';

export interface NoticeSheetProps {
  /** Coral when something stands in the way; the accent for the player's own; the soft ink when it only asks. */
  tone: 'danger' | 'accent' | 'neutral';
  /** The round mark's drawing, at a notice's size (ToneMark lg), in its tone: the plane mark, laid on its side, on the session's end (PLANE_MARK). */
  glyph: Drawing;
  /** What the notice is about, over the title in the accent's lettering: the section a page's problem is in. */
  kicker?: ReactNode;
  title: ReactNode;
  /** What happened, in one block of words, what happens next with it. It describes the notice to assistive technology. */
  description: ReactNode;
  /** One fact on a well under the words: what it is, and its value at the far end. */
  fact?: { label: ReactNode; value?: ReactNode };
  /** Folded under the words, a Disclosure: the message a failure threw. */
  details?: ReactNode;
  /** The buttons along its foot, the decisive one last; a lone one runs the width of the words. */
  actions: ReactNode;
  /** A quiet line at its very foot, where the words start: the version line. */
  foot?: ReactNode;
  /** The clock along its foot, while the notice waits to act by itself: from where its time stands as the notice opens. */
  clock?: FootClockProps;
  /** Laid on the page, an alert: heard as it appears. A dialog that wraps the sheet is its own role. */
  role?: 'alert';
  /** The ids of its title and sentence, for a dialog that wraps the sheet and is named by them. */
  titleId?: string;
  descriptionId?: string;
}

export function NoticeSheet({
  tone,
  glyph,
  kicker,
  title,
  description,
  fact,
  details,
  actions,
  foot,
  clock,
  role,
  titleId,
  descriptionId,
}: NoticeSheetProps) {
  const ownTitleId = useId();
  const ownDescriptionId = useId();
  const named = titleId ?? ownTitleId;
  const described = descriptionId ?? ownDescriptionId;
  return (
    <div
      className={styles.notice}
      role={role}
      aria-labelledby={role ? named : undefined}
      aria-describedby={role ? described : undefined}
    >
      <div className={styles.body}>
        <ToneMark tone={tone} size="lg" glyph={glyph} />
        <div className={utilities.besideMarkWords}>
          {kicker && <p className={styles.kicker}>{kicker}</p>}
          <h2 id={named} className={styles.title}>
            {title}
          </h2>
          <p id={described} className={styles.description}>
            {description}
          </p>
          {fact && (
            <div className={styles.fact}>
              <HoverCardFact label={fact.label} value={fact.value} />
            </div>
          )}
          {details && <div className={styles.details}>{details}</div>}
        </div>
      </div>
      <div className={styles.actions}>{actions}</div>
      {foot && <div className={styles.foot}>{foot}</div>}
      {clock && <FootClock {...clock} />}
    </div>
  );
}
